import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';
import ConfirmAbortDialog from '@common/components/General/ConfirmAbortDialog';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { dateToStringFormat } from '@common/utils/date';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectRobotsData } from '../../../store/robots/robots-slice-selectors';
import { reassignMissionsByRobotId } from '../../../store/robots/robots-actions';
import organizationsDataService from '../../../services/data/organizations-data-service';
import { DeploymentDetails } from '../../../models/organization/deployment';
import { MissionOutsideRange } from '../../../models/missions/mission-outside-range';
import SelectInput from 'components/forms/SelectInput';

enum RangeFilter {
  ALL,
  BEFORE,
  AFTER,
  CHANGED,
}

const deploymentEnd = (deployment: DeploymentDetails): number =>
  deployment.end != null ? DateTime.fromISO(deployment.end).toMillis() : Date.now();

export const isMissionStartOutsideDeployment = (
  missionStartIso: string,
  deployment: DeploymentDetails,
): boolean => {
  const start = DateTime.fromISO(missionStartIso).toMillis();
  return start < DateTime.fromISO(deployment.start).toMillis() || start > deploymentEnd(deployment);
};

export const deploymentHasMissionsOutsideRange = (deployment: DeploymentDetails): boolean => {
  const start = DateTime.fromISO(deployment.start).toMillis();
  const effectiveEnd = deploymentEnd(deployment);
  if (
    deployment.firstMissionDate != null &&
    DateTime.fromISO(deployment.firstMissionDate).toMillis() < start
  ) {
    return true;
  }
  if (
    deployment.lastMissionDate != null &&
    DateTime.fromISO(deployment.lastMissionDate).toMillis() > effectiveEnd
  ) {
    return true;
  }
  return false;
};

const deploymentLabel = (deployment: DeploymentDetails, ongoingLabel: string): string => {
  const start = dateToStringFormat(DateTime.fromISO(deployment.start), true);
  const end =
    deployment.end == null
      ? ongoingLabel
      : dateToStringFormat(DateTime.fromISO(deployment.end), true);
  return `${deployment.organizationName} · ${start} – ${end}`;
};

interface ReassignMissionsDialogProps {
  open: boolean;
  onClose: () => void;
  robotId: string;
  deployment: DeploymentDetails;
  deployments: DeploymentDetails[];
}

const ReassignMissionsDialog: React.FC<ReassignMissionsDialogProps> = ({
  open,
  onClose,
  robotId,
  deployment,
  deployments,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));

  const [missions, setMissions] = useState<MissionOutsideRange[]>([]);
  const [fetchStatus, setFetchStatus] = useState<HttpFetchStatus>(HttpFetchStatus.Idle);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingTargets, setPendingTargets] = useState<Record<string, string>>({});
  const [targetDeploymentId, setTargetDeploymentId] = useState<string>('');
  const [filter, setFilter] = useState<RangeFilter>(RangeFilter.ALL);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const deploymentById = useMemo(() => {
    const map: Record<string, DeploymentDetails> = {};
    deployments.forEach((d) => (map[d.id] = d));
    return map;
  }, [deployments]);

  // Fetch the deployment's outside-range missions when the dialog opens.
  // Fetched in the component as is only used here, so not needed in state
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFetchStatus(HttpFetchStatus.Loading);
    organizationsDataService
      .getMissionsOutsideRangeByDeployment(robotId, deployment.id)
      .then((data) => {
        if (cancelled) return;
        setMissions(data);
        setFetchStatus(HttpFetchStatus.Success);
      })
      .catch(() => {
        if (!cancelled) setFetchStatus(HttpFetchStatus.Failed);
      });
    return () => {
      cancelled = true;
    };
  }, [open, robotId, deployment.id]);

  useEffect(() => {
    if (robotsData.rest.reassignMissionsStatus === HttpFetchStatus.Success && open) {
      onClose();
    }
  }, [onClose, open, robotsData.rest.reassignMissionsStatus]);

  const effectiveDeployment = (mission: MissionOutsideRange): DeploymentDetails =>
    deploymentById[pendingTargets[mission.id] ?? mission.deploymentId] ?? deployment;

  const hasPendingChange = (mission: MissionOutsideRange): boolean =>
    pendingTargets[mission.id] != null && pendingTargets[mission.id] !== mission.deploymentId;

  const changedReassignments = missions
    .filter((mission) => hasPendingChange(mission))
    .map((mission) => ({ id: mission.id, deploymentId: pendingTargets[mission.id] }));

  const visibleMissions = missions.filter((mission) => {
    switch (filter) {
      case RangeFilter.BEFORE:
        return (
          DateTime.fromISO(mission.startTime).toMillis() <
          DateTime.fromISO(deployment.start).toMillis()
        );
      case RangeFilter.AFTER:
        return DateTime.fromISO(mission.startTime).toMillis() > deploymentEnd(deployment);
      case RangeFilter.CHANGED:
        return hasPendingChange(mission);
      default:
        return true;
    }
  });

  const toggleMission = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = visibleMissions.every((mission) => next.has(mission.id));
      visibleMissions.forEach((mission) =>
        allSelected ? next.delete(mission.id) : next.add(mission.id),
      );
      return next;
    });
  };

  const assignToSelected = () => {
    if (targetDeploymentId === '' || selectedIds.size === 0) return;
    setPendingTargets((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => (next[id] = targetDeploymentId));
      return next;
    });
    setSelectedIds(new Set());
  };

  const resetLocalState = () => {
    setPendingTargets({});
    setSelectedIds(new Set());
    setTargetDeploymentId('');
    setFilter(RangeFilter.ALL);
  };

  const handleClose = () => {
    if (changedReassignments.length > 0) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const confirmSave = () => {
    setShowSaveConfirm(false);
    dispatch(reassignMissionsByRobotId({ robotId, reassignments: changedReassignments }));
  };

  const allVisibleSelected =
    visibleMissions.length > 0 && visibleMissions.every((m) => selectedIds.has(m.id));

  // How many of the dialog's missions would fall inside a given deployment's date range.
  const missionsCoveredBy = (option: DeploymentDetails): number =>
    missions.filter((mission) => !isMissionStartOutsideDeployment(mission.startTime, option))
      .length;

  return (
    <React.Fragment>
      <DialogWrapperWithClose
        onClose={handleClose}
        open={open}
        title={t('title-reassign-missions')}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          <Stack spacing={2}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'background.paper',
                py: 1,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Box sx={{ minWidth: '264px' }}>
                  <SelectInput
                    label={t('input-label-target-deployment')}
                    value={targetDeploymentId}
                    onChange={(event) => setTargetDeploymentId(event.target.value as string)}
                  >
                    {deployments.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {`${deploymentLabel(option, t('info-label-ongoing'))} · ${t(
                          'label-missions-in-range',
                          {
                            count: missionsCoveredBy(option),
                          },
                        )}`}
                      </MenuItem>
                    ))}
                  </SelectInput>
                </Box>
                <Button
                  variant="contained"
                  onClick={assignToSelected}
                  disabled={targetDeploymentId === '' || selectedIds.size === 0}
                  data-testid="reassign-assign-button"
                >
                  {t('button-assign-to-selected')}
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" mt={1} flexWrap="wrap">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={filter}
                  onChange={(_event, value: RangeFilter | null) =>
                    value != null && setFilter(value)
                  }
                >
                  <ToggleButton value={RangeFilter.ALL}>{t('filter-missions-all')}</ToggleButton>
                  <ToggleButton value={RangeFilter.BEFORE}>
                    {t('filter-missions-before-start')}
                  </ToggleButton>
                  <ToggleButton value={RangeFilter.AFTER}>
                    {t('filter-missions-after-end')}
                  </ToggleButton>
                  <ToggleButton value={RangeFilter.CHANGED}>
                    {t('filter-missions-changed')}
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  size="small"
                  onClick={selectAllVisible}
                  disabled={visibleMissions.length === 0}
                >
                  {allVisibleSelected ? t('button-deselect-all') : t('button-select-all')}
                </Button>
              </Stack>
            </Box>

            {fetchStatus === HttpFetchStatus.Failed && (
              <CustomAlert severity="error" header={t('error-loading-missions-outside-range')} />
            )}
            {fetchStatus === HttpFetchStatus.Success && missions.length === 0 && (
              <Typography variant="body2">{t('info-no-missions-outside-range')}</Typography>
            )}

            {missions.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('table-header-mission-start')}</TableCell>
                    <TableCell>{t('table-header-mission-deployment')}</TableCell>
                    <TableCell>{t('table-header-mission-room')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleMissions.map((mission) => {
                    const target = effectiveDeployment(mission);
                    const stillOutside = isMissionStartOutsideDeployment(mission.startTime, target);
                    return (
                      <TableRow key={mission.id} data-testid={`reassign-row-${mission.id}`}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(mission.id)}
                            onChange={() => toggleMission(mission.id)}
                            inputProps={
                              {
                                'data-testid': `reassign-checkbox-${mission.id}`,
                              } as React.InputHTMLAttributes<HTMLInputElement>
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {DateTime.fromISO(mission.startTime).toLocaleString(
                            DateTime.DATETIME_SHORT,
                          )}
                        </TableCell>
                        <TableCell>{deploymentLabel(target, t('info-label-ongoing'))}</TableCell>
                        <TableCell>{mission.roomName ?? t('unknown-room-name')}</TableCell>
                        <TableCell>
                          {stillOutside && (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              color="warning.main"
                            >
                              <WarningAmberIcon fontSize="small" />
                              <Typography variant="caption">
                                {t('warning-mission-still-outside-range')}
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetLocalState} disabled={changedReassignments.length === 0}>
            {t('button-discard')}
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowSaveConfirm(true)}
            disabled={
              changedReassignments.length === 0 ||
              robotsData.rest.reassignMissionsStatus === HttpFetchStatus.Loading
            }
            data-testid="reassign-save-button"
          >
            {t('button-save')}
          </Button>
        </DialogActions>
      </DialogWrapperWithClose>

      <ConfirmAbortDialog
        isOpen={showSaveConfirm}
        title={t('title-confirm-reassign-missions')}
        description={t('info-confirm-reassign-missions', { count: changedReassignments.length })}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-save')}
        onConfirm={confirmSave}
        onAbort={() => setShowSaveConfirm(false)}
      />
      <ConfirmAbortDialog
        isOpen={showDiscardConfirm}
        title={t('title-discard-changes')}
        description={t('info-discard-reassign-changes')}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-discard')}
        onConfirm={() => {
          setShowDiscardConfirm(false);
          resetLocalState();
          onClose();
        }}
        onAbort={() => setShowDiscardConfirm(false)}
      />
    </React.Fragment>
  );
};

export default ReassignMissionsDialog;
